/**
 * Helpers para os editores de texto formatado (laudos, atestados,
 * declarações, encaminhamentos, notas). O conteúdo persiste em Firestore
 * como string — pode ser texto puro (formato antigo) ou HTML (formato
 * novo, gerado pelo RichTextEditor). Os helpers aqui encapsulam a
 * detecção de HTML, conversão entre os dois formatos e a sanitização
 * antes de qualquer renderização ou envio à IA.
 */

/** Heurística simples — basta uma tag HTML válida para tratarmos como HTML. */
export function looksLikeHtml(text: string): boolean {
  return /<\/?[a-zA-Z][\s\S]*?>/.test(text);
}

/** Escapa caracteres perigosos antes de inserir em HTML. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Converte texto puro em HTML preservando quebras de linha. Usado quando
 * carregamos um conteúdo antigo (texto puro) no editor formatado.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return '';
  return escapeHtml(text).replace(/\r\n|\n|\r/g, '<br>');
}

/**
 * Converte HTML em texto puro — usado para enviar ao LLM (manter o
 * mesmo input que existia antes da migração para HTML, evitando ruído
 * de tags no prompt) e para qualquer outro consumidor que espere texto.
 *
 * Em ambiente de browser usamos o parser nativo (preserva acentos,
 * lida com entidades). Fora do browser caímos em uma regex simples.
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  if (!looksLikeHtml(html)) return html;
  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    // <br> e </div>/<p> viram \n para preservar quebras visuais.
    doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    // Tabelas: células separadas por " | " e cada linha em uma linha de texto
    // — sem isto o conteúdo das células sairia tudo emendado.
    doc.querySelectorAll('td, th').forEach((cell) => {
      if (cell.nextElementSibling) cell.append(' | ');
    });
    doc.querySelectorAll('tr').forEach((row) => row.append('\n'));
    doc.querySelectorAll('div, p').forEach((el) => {
      if (!el.textContent?.endsWith('\n')) el.append('\n');
    });
    return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
  }
  // Fallback (ex: ambiente de teste sem DOM): regex bem básica.
  return html
    .replace(/<br\s*\/?>(\s)*/gi, '\n')
    .replace(/<\/(div|p)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Sanitiza HTML mantendo apenas tags e estilos seguros de formatação.
 *
 * Whitelist intencionalmente curta — cobre o que o RichTextEditor pode
 * gerar (negrito/itálico/sublinhado, alinhamento, tamanho de fonte) e
 * blocos de parágrafo. Qualquer outra tag (incluindo `<script>`, `<img
 * src=...>`, `<iframe>`, eventos `on*`) é removida.
 *
 * Pré-condição: roda no browser (usa DOMParser). Fora do browser
 * retorna o input intocado — a sanitização real será aplicada na próxima
 * chamada que rodar com DOM disponível.
 */
const ALLOWED_TAGS = new Set([
  'b', 'strong', 'i', 'em', 'u', 'br', 'p', 'div', 'span', 'ul', 'ol', 'li',
  // Tabelas (modelos tipo gonioscopia + botão "Inserir tabela" do editor).
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
]);

const ALLOWED_STYLE_PROPS = new Set([
  'font-weight',
  'font-style',
  'text-decoration',
  'text-decoration-line',
  'font-size',
  'text-align',
  // Estilos de tabela — bordas visíveis no editor e na impressão.
  'border',
  'border-collapse',
  'width',
  'padding',
  'vertical-align',
  // Texto em múltiplas colunas (botão "Colunas" do editor).
  'column-count',
  'column-gap',
  'column-rule',
]);

function sanitizeStyle(style: string): string {
  return style
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const colon = decl.indexOf(':');
      if (colon < 0) return false;
      const prop = decl.slice(0, colon).trim().toLowerCase();
      const value = decl.slice(colon + 1).trim();
      // Bloqueia url(), expression(), javascript:.
      if (/url\s*\(|expression\s*\(|javascript:/i.test(value)) return false;
      return ALLOWED_STYLE_PROPS.has(prop);
    })
    .join('; ');
}

function sanitizeNode(node: Element): void {
  const tag = node.tagName.toLowerCase();
  if (!ALLOWED_TAGS.has(tag)) {
    // Remove a tag mas preserva o conteúdo interno (move filhos pra fora).
    const parent = node.parentNode;
    if (parent) {
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
    }
    return;
  }
  // Remove atributos perigosos; mantém apenas `style` filtrado.
  for (const attr of Array.from(node.attributes)) {
    if (attr.name.toLowerCase() === 'style') {
      const safe = sanitizeStyle(attr.value);
      if (safe) node.setAttribute('style', safe);
      else node.removeAttribute('style');
    } else {
      node.removeAttribute(attr.name);
    }
  }
  // Recurse — copiamos a lista pois remoções podem alterar children durante o loop.
  for (const child of Array.from(node.children)) sanitizeNode(child);
}

export function sanitizeRichHtml(html: string): string {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return html;
  }
  const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';
  for (const child of Array.from(root.children)) sanitizeNode(child);
  return root.innerHTML;
}

/**
 * Remove `font-size` de todos os elementos descendentes de `root` e
 * desembrulha `<span>` que ficarem sem nenhum atributo. Muta `root` in-place.
 *
 * Usado pelo RichTextEditor ao aplicar um tamanho a uma seleção: o trecho é
 * embrulhado num `<span style="font-size:…">`, mas qualquer `font-size` inline
 * mais interno (resíduo de edições anteriores) venceria o do wrapper — o texto
 * sairia com tamanhos mistos mesmo depois de "selecionar tudo e aplicar".
 * Zerando os tamanhos internos, o wrapper passa a uniformizar de fato.
 */
export function stripDescendantFontSize(root: HTMLElement): void {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[style]'))) {
    if (el.style.fontSize) el.style.removeProperty('font-size');
    // Remover a última propriedade deixa `style=""` (o atributo não some
    // sozinho); apagamos pra que o span possa ser desembrulhado abaixo.
    if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
  }
  // Spans que sobraram sem atributos são só ruído — desembrulha preservando o
  // conteúdo. querySelectorAll devolve em ordem de documento, então um span-pai
  // já desembrulhado deixa o filho com `parentNode` atualizado.
  for (const span of Array.from(root.querySelectorAll('span'))) {
    if (span.attributes.length > 0) continue;
    const parent = span.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
  }
}
